
import React, { useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Progress } from '@/components/ui/progress';
import { Upload, AlertCircle, CheckCircle, Image as ImageIcon } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface UploadResult {
  filename: string;
  status: 'success' | 'error';
  profileId?: string;
  username?: string;
  error?: string;
}

const BulkProfileImageUpload = () => {
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<UploadResult[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) {
      return;
    }
    
    // Convert FileList to array
    const fileArray = Array.from(event.target.files);
    setFiles(fileArray);
    setResults([]);
    
    toast({
      title: `${fileArray.length} images selected`,
      description: "Images will be matched to profiles by filename/username.",
    });
  };

  const processFiles = async () => {
    if (files.length === 0) {
      toast({
        title: "No files selected",
        description: "Please select image files to upload.",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    setProgress(0);
    setResults([]);

    try {
      // Fetch all profiles once to reduce DB queries
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, username');

      if (profilesError) {
        throw new Error(`Failed to fetch profiles: ${profilesError.message}`);
      }

      const uploadResults: UploadResult[] = [];
      let completedUploads = 0;

      // Process each file
      for (const file of files) {
        try {
          const fileName = file.name.split('.')[0].toLowerCase();
          
          // Find matching profile by username (case insensitive)
          const matchingProfile = profiles.find(
            profile => profile.username && profile.username.toLowerCase() === fileName
          );

          if (!matchingProfile) {
            uploadResults.push({
              filename: file.name,
              status: 'error',
              error: 'No matching profile found with this username'
            });
            completedUploads++;
            setProgress(Math.round((completedUploads / files.length) * 100));
            continue;
          }

          // Create a unique filename with user ID
          const fileExt = file.name.split('.').pop();
          const storagePath = `${matchingProfile.id}/${Date.now()}.${fileExt}`;

          // Upload to the avatars bucket
          const { error: uploadError } = await supabase.storage
            .from('avatars')
            .upload(storagePath, file, {
              cacheControl: '3600',
              upsert: true
            });

          if (uploadError) {
            uploadResults.push({
              filename: file.name,
              status: 'error',
              profileId: matchingProfile.id,
              username: matchingProfile.username,
              error: uploadError.message
            });
            completedUploads++;
            setProgress(Math.round((completedUploads / files.length) * 100));
            continue;
          }

          // Get the public URL
          const { data: publicUrlData } = supabase.storage
            .from('avatars')
            .getPublicUrl(storagePath);

          // Update the profile with the new avatar URL
          const { error: updateError } = await supabase
            .from('profiles')
            .update({ avatar_url: publicUrlData.publicUrl })
            .eq('id', matchingProfile.id);

          if (updateError) {
            uploadResults.push({
              filename: file.name,
              status: 'error',
              profileId: matchingProfile.id,
              username: matchingProfile.username,
              error: `Profile updated failed: ${updateError.message}`
            });
          } else {
            uploadResults.push({
              filename: file.name,
              status: 'success',
              profileId: matchingProfile.id,
              username: matchingProfile.username
            });
          }
        } catch (error: any) {
          uploadResults.push({
            filename: file.name,
            status: 'error',
            error: error.message || 'Unknown error'
          });
        }

        completedUploads++;
        setProgress(Math.round((completedUploads / files.length) * 100));
      }

      setResults(uploadResults);
      
      const successCount = uploadResults.filter(r => r.status === 'success').length;
      toast({
        title: "Bulk upload completed",
        description: `Successfully updated ${successCount} out of ${files.length} profile images.`,
        variant: successCount === files.length ? "default" : "destructive",
      });

    } catch (error: any) {
      console.error('Error during bulk upload:', error);
      toast({
        title: "Bulk upload failed",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      setProgress(100);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Bulk Profile Image Upload</CardTitle>
        <CardDescription>
          Upload multiple profile pictures at once. Images will be matched to profiles by their filename,
          which should match the username exactly (e.g., "johndoe.jpg" for user "johndoe").
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <input
              type="file"
              ref={fileInputRef}
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleFileChange}
              multiple
              className="hidden"
              disabled={uploading}
            />
            
            <Button 
              onClick={() => fileInputRef.current?.click()} 
              variant="outline"
              disabled={uploading}
              className="flex items-center gap-2"
            >
              <ImageIcon className="h-4 w-4" />
              {files.length > 0 ? `${files.length} images selected` : 'Select Profile Images'}
            </Button>
            
            <Button 
              onClick={processFiles} 
              disabled={files.length === 0 || uploading}
              className="flex items-center gap-2"
            >
              {uploading ? 'Processing...' : 'Upload & Process'}
              {!uploading && <Upload className="h-4 w-4" />}
            </Button>
          </div>

          {uploading && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Uploading and processing images...</span>
                <span>{progress}%</span>
              </div>
              <Progress value={progress} className="w-full" />
            </div>
          )}

          {files.length > 0 && !uploading && (
            <Alert>
              <ImageIcon className="h-4 w-4" />
              <AlertTitle>Ready to upload</AlertTitle>
              <AlertDescription>
                {files.length} image{files.length !== 1 ? 's' : ''} selected. 
                Make sure each filename matches exactly with a username in the system.
              </AlertDescription>
            </Alert>
          )}

          {results.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Upload Results</h3>
              
              <div className="space-y-2">
                <div className="text-sm font-medium">
                  Summary: {results.filter(r => r.status === 'success').length} succeeded, {' '}
                  {results.filter(r => r.status === 'error').length} failed
                </div>
                <div className="max-h-60 overflow-y-auto space-y-2 border rounded-md p-2">
                  {results.map((result, index) => (
                    <div 
                      key={index}
                      className={`flex items-start gap-2 p-2 rounded text-sm ${
                        result.status === 'success' 
                          ? 'bg-green-50 text-green-700 border border-green-200' 
                          : 'bg-red-50 text-red-700 border border-red-200'
                      }`}
                    >
                      {result.status === 'success' ? (
                        <CheckCircle className="h-4 w-4 mt-0.5 shrink-0" />
                      ) : (
                        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                      )}
                      <div className="flex-1">
                        <div className="font-medium">{result.filename}</div>
                        {result.username && (
                          <div className="text-xs">Username: {result.username}</div>
                        )}
                        {result.error && (
                          <div className="text-xs mt-1 font-medium">Error: {result.error}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default BulkProfileImageUpload;
